// @core/stores/authn/AuthnStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { authnApi } from '@core/api';
import type {
  AuthorizeDeviceRequest,
  AuthorizeDeviceResponse,
  AuthorizeRequest,
  AuthorizeResponse,
  CreateOAuth2ClientRequest,
  CreateOAuth2ClientResponse,
  CreateOAuth2ProviderRequest,
  CreateSSOSessionRequest,
  CreateSSOSessionResponse,
  ExchangeChannelTokenRequest,
  ExchangeDeviceTokenRequest,
  ExchangeTokenRequest,
  GetUserInfoResponse,
  GrantOAuth2ConsentRequest,
  HandleOAuth2CallbackRequest,
  HandleOAuth2CallbackResponse,
  InitiateOAuth2FlowRequest,
  InitiateOAuth2FlowResponse,
  IntrospectTokenRequest,
  IntrospectTokenResponse,
  LinkOAuth2AccountRequest,
  ListOAuth2ClientsResponse,
  ListOAuth2ConnectionsResponse,
  ListOAuth2ConsentsResponse,
  ListOAuth2ProvidersResponse,
  ListPublicOAuth2ProvidersResponse,
  OAuth2ClientResponse,
  OAuth2ConsentResponse,
  OAuth2ProviderResponse,
  RevokeTokenRequest,
  RotateOAuth2ClientSecretRequest,
  RotateOAuth2ClientSecretResponse,
  SSOSession,
  TerminateSSOSessionRequest,
  TokenResponse,
  UnlinkOAuth2AccountRequest,
  UpdateOAuth2ClientRequest,
  UpdateOAuth2ProviderRequest,
  ValidateSSOSessionRequest,
  ValidateSSOSessionResponse,
} from '@core/types/authn';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AuthnStore {
  // List state
  items: SSOSession[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: SSOSession | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, SSOSession>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): SSOSession | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: SSOSession | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Initiate OAuth2 authorization flow
   */
  async Authorize(data: AuthorizeRequest): Promise<AuthorizeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.Authorize(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Authorize device (OAuth2 Device Flow)
   */
  async AuthorizeDevice(data: AuthorizeDeviceRequest): Promise<AuthorizeDeviceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.AuthorizeDevice(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create OAuth2 client
   */
  async CreateOAuth2Client(data: CreateOAuth2ClientRequest): Promise<CreateOAuth2ClientResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.CreateOAuth2Client(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create OAuth2 provider
   */
  async CreateOAuth2Provider(data: CreateOAuth2ProviderRequest): Promise<OAuth2ProviderResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.CreateOAuth2Provider(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create SSO session
   */
  async CreateSSOSession(data: CreateSSOSessionRequest): Promise<CreateSSOSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.CreateSSOSession(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete OAuth2 client
   */
  async DeleteOAuth2Client(client_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await authnApi.DeleteOAuth2Client(client_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== client_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === client_id) {
          this.currentItem = null;
        }

        this.removeFromCache(client_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete OAuth2 provider
   */
  async DeleteOAuth2Provider(provider_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await authnApi.DeleteOAuth2Provider(provider_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== provider_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === provider_id) {
          this.currentItem = null;
        }

        this.removeFromCache(provider_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Exchange a channel identity for tokens
   */
  async ExchangeChannelToken(data: ExchangeChannelTokenRequest): Promise<TokenResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ExchangeChannelToken(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Exchange device code for tokens
   */
  async ExchangeDeviceToken(data: ExchangeDeviceTokenRequest): Promise<TokenResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ExchangeDeviceToken(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Exchange authorization code for tokens
   */
  async ExchangeToken(data: ExchangeTokenRequest): Promise<TokenResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ExchangeToken(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get OAuth2 client details
   */
  async GetOAuth2Client(client_id: string): Promise<OAuth2ClientResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.GetOAuth2Client(client_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get OAuth2 consent details
   */
  async GetOAuth2Consent(client_id: string): Promise<OAuth2ConsentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.GetOAuth2Consent(client_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get OAuth2 provider details
   */
  async GetOAuth2Provider(provider_id: string): Promise<OAuth2ProviderResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.GetOAuth2Provider(provider_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get user information (OIDC UserInfo endpoint)
   */
  async GetUserInfo(params?: { }): Promise<GetUserInfoResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.GetUserInfo(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Grant OAuth2 consent
   */
  async GrantOAuth2Consent(data: GrantOAuth2ConsentRequest): Promise<OAuth2ConsentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.GrantOAuth2Consent(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Handle OAuth2 callback from external provider
   */
  async HandleOAuth2Callback(data: HandleOAuth2CallbackRequest): Promise<HandleOAuth2CallbackResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.HandleOAuth2Callback(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Initiate external OAuth2 flow
   */
  async InitiateOAuth2Flow(data: InitiateOAuth2FlowRequest): Promise<InitiateOAuth2FlowResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.InitiateOAuth2Flow(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Introspect OAuth2 token
   */
  async IntrospectToken(data: IntrospectTokenRequest): Promise<IntrospectTokenResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.IntrospectToken(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Link OAuth2 account to user
   */
  async LinkOAuth2Account(data: LinkOAuth2AccountRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.LinkOAuth2Account(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List OAuth2 clients
   */
  async ListOAuth2Clients(params?: PaginatedRequest): Promise<ListOAuth2ClientsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ListOAuth2Clients(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List OAuth2 connections for user
   */
  async ListOAuth2Connections(params?: PaginatedRequest): Promise<ListOAuth2ConnectionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ListOAuth2Connections(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List OAuth2 consents for user
   */
  async ListOAuth2Consents(params?: PaginatedRequest): Promise<ListOAuth2ConsentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ListOAuth2Consents(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List OAuth2 providers
   */
  async ListOAuth2Providers(params?: PaginatedRequest): Promise<ListOAuth2ProvidersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ListOAuth2Providers(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List public OAuth2 providers
   */
  async ListPublicOAuth2Providers(params?: PaginatedRequest): Promise<ListPublicOAuth2ProvidersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ListPublicOAuth2Providers(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List SSO sessions for user
   */
  async ListSSOSessions(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await authnApi.ListSSOSessions(params);

      runInAction(() => {
        this.items = response.sessions?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke OAuth2 consent
   */
  async RevokeOAuth2Consent(client_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.RevokeOAuth2Consent(client_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke OAuth2 token
   */
  async RevokeToken(data: RevokeTokenRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.RevokeToken(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Rotate OAuth2 client secret
   */
  async RotateOAuth2ClientSecret(client_id: string, data: RotateOAuth2ClientSecretRequest): Promise<RotateOAuth2ClientSecretResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.RotateOAuth2ClientSecret(client_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Terminate SSO session
   */
  async TerminateSSOSession(session_id: string, data: TerminateSSOSessionRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.TerminateSSOSession(session_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unlink OAuth2 account from user
   */
  async UnlinkOAuth2Account(data: UnlinkOAuth2AccountRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.UnlinkOAuth2Account(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update OAuth2 client
   */
  async UpdateOAuth2Client(client_id: string, data: UpdateOAuth2ClientRequest): Promise<OAuth2ClientResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.UpdateOAuth2Client(client_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update OAuth2 provider
   */
  async UpdateOAuth2Provider(provider_id: string, data: UpdateOAuth2ProviderRequest): Promise<OAuth2ProviderResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.UpdateOAuth2Provider(provider_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate SSO session
   */
  async ValidateSSOSession(data: ValidateSSOSessionRequest): Promise<ValidateSSOSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authnApi.ValidateSSOSession(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const authnStore = new AuthnStore();
